import React from 'react';
import { Button, Form } from 'react-bootstrap';

interface PermissionAction {
  id: number;
  action: string;
  status: boolean;
}

interface PermissionGroup {
  name: string;
  actions: PermissionAction[];
}

interface PermissionThresholdProps {
  permissions: PermissionGroup[];
  selectedPermissions: Record<string, number[]>;
  handlePermissionChange: (name: string, actionId: number) => void;
  isGroupAllSelected: (name: string) => boolean;
  handleGroupSelectAll: (name: string) => void;
  handleSectionSelectAll: (groupNames: string[]) => void;
  isListDisabled: (name: string) => boolean;
  formErrors: {
    permissions?: string;
  };
}

const PermissionThreshold: React.FC<PermissionThresholdProps> = ({
  permissions,
  selectedPermissions,
  handlePermissionChange,
  isGroupAllSelected,
  handleGroupSelectAll,
  handleSectionSelectAll,
  isListDisabled,
  formErrors,
}) => {
  // Define the group arrays
  const masterGroups = [
    "Grade Groups",
    "Product Groups",
    "Products",
    "Locations",
    "Districts",
    "States",
    "Roles",
    "Users",
    "Copy Freights",
    "Transporters",
  ];

  const reportsGroups = [
    "Usage Analysis",
    "Order Duration Analysis",
    "Inquiry to Invoice Analysis",
    "Grade Group Wise Report",
    "Near to Expiry Offers",
    "Active Inquiries",
    "Recently Completed Inquiries",
    "User Wise Inquiry",
    "Operation Report",
    "Message Logs"
  ];

  const manageNewsGroups = [
    "News Articles",
    "Category"
  ];

  const settingsGroups = [
    "Settings",
    "Settings - SGST",
    "Settings - CGST",
    "Settings - IGST",
    "Settings - T&C For Quotation",
    "Settings - T&C For PO",
    "Settings - T&C For Buyer",
    "Settings - T&C For Seller",
    "Settings - T&C For Add Inquiry",
    "Settings - Graph-Buyer/Seller"
  ];

  const formatActionLabel = (action: string): string => {
    const transformedAction = action.toLowerCase() === 'create' ? 'add' : action;
    return transformedAction.charAt(0).toUpperCase() + transformedAction.slice(1);
  };

  // Function to render individual permission groups
  const renderPermissionGroups = (groupNames: string[], parentId: string) => {
    return permissions
      .filter(group => groupNames.includes(group.name))
      .map(group => {
        const safeId = group.name.replace(/[^a-zA-Z0-9]/g, '-');
        return (
          <div key={group.name} className='col-md-6 col-lg-4'>
            <div className='threshold-item accordion-item border rounded-3 shadow-sm-soft'>
              <div className='d-flex align-items-start px-4 py-3'>
                <button
                  className='threshold-button accordion-button collapsed d-flex align-items-center px-4 py-3 flex-grow-1'
                  type='button'
                  data-bs-toggle='collapse'
                  data-bs-target={`#accordion-body-${safeId}`}
                  aria-expanded='false'
                  aria-controls={`accordion-body-${safeId}`}
                  style={{ position: 'relative', zIndex: 2 }}
                >
                  <span className='flex-grow-1 d-flex align-items-center gap-2'>
                    {group.name}
                    <i className='arrow-icon bi bi-chevron-down ms-1'></i>
                  </span>
                </button>
                <label
                  className='form-check form-check-custom form-check-solid ms-2 d-flex flex-column'
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: 'relative', zIndex: 3 }}
                >
                    <input
                      type='checkbox'
                      id={`select-all-${safeId}`}
                      checked={isGroupAllSelected(group.name)}
                      onChange={() => handleGroupSelectAll(group.name)}
                      className='form-check-input mb-1'
                    />
                    All
                </label>
              </div>
              <div
                id={`accordion-body-${safeId}`}
                className='accordion-collapse collapse'
                aria-labelledby={`accordion-header-${safeId}`}
                data-bs-parent={`#${parentId}`}
              >
                <div className='accordion-body px-4 py-3'>
                  <div className='d-flex flex-column gap-2'>
                    {group.actions.map((action) => (
                      <div key={action.id} className='form-check form-check-custom form-check-solid'>
                        <Form.Check
                          type='checkbox'
                          id={`${safeId}-${action.id}`}
                          checked={selectedPermissions[group.name]?.includes(action.id) || false}
                          onChange={() => handlePermissionChange(group.name, action.id)}
                          label={formatActionLabel(action.action)}
                          disabled={action.action === 'list' && isListDisabled(group.name)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      });
  };

  // Function to render a threshold section
  const renderThresholdSection = (title: string, groupNames: string[], id: string) => (
    <div className='col-12 mb-4'>
      <div className='threshold-item accordion-item border rounded-3 shadow-sm-soft'>
        <div className='d-flex align-items-center px-4 py-3'>
          <button
            className='threshold-button accordion-button collapsed d-flex align-items-center px-4 py-3 flex-grow-1'
            type='button'
            data-bs-toggle='collapse'
            data-bs-target={`#accordion-body-${id}`}
            aria-expanded='false'
            aria-controls={`accordion-body-${id}`}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span className='flex-grow-1 d-flex align-items-center gap-2'>
              {title}
              <i className='arrow-icon bi bi-chevron-down ms-1'></i>
            </span>
          </button>
          <label
            className='form-check form-check-custom form-check-solid ms-2 d-flex flex-column'
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', zIndex: 3 }}
          >
            <input
              type='checkbox'
              id={`select-all-${id}`}
              checked={groupNames.every(name => isGroupAllSelected(name))}
              onChange={() => handleSectionSelectAll(groupNames)}
               className='form-check-input mb-1'
            />
             All
          </label>
        </div>
        <div
          id={`accordion-body-${id}`}
          className='accordion-collapse collapse'
          aria-labelledby={`accordion-header-${id}`}
          data-bs-parent='#permissionsAccordion'
        >
          <div className='accordion-body px-4 py-3'>
            <div className='row g-4' id={`${id}-groups`}>
              {renderPermissionGroups(groupNames, `${id}-groups`)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className='row g-4 custom-threshold-accordion' id='permissionsAccordion'>
      {/* Other Permission Groups */}
      {permissions
        .filter(group => 
          !masterGroups.includes(group.name) && 
          !reportsGroups.includes(group.name) &&
          !manageNewsGroups.includes(group.name) &&
          !settingsGroups.includes(group.name)
        )
        .map(group => {
          const safeId = group.name.replace(/[^a-zA-Z0-9]/g, '-');
          return (
            <div key={group.name} className='col-md-6 col-lg-4'>
              <div className='threshold-item accordion-item border rounded-3 shadow-sm-soft'>
                <div className='d-flex align-items-start px-4 py-3'>
                  <button
                    className='threshold-button accordion-button collapsed d-flex align-items-center px-4 py-3 flex-grow-1'
                    type='button'
                    data-bs-toggle='collapse'
                    data-bs-target={`#accordion-body-${safeId}`}
                    aria-expanded='false'
                    aria-controls={`accordion-body-${safeId}`}
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    <span className='flex-grow-1 d-flex align-items-center gap-2'>
                      {group.name}
                      <i className='arrow-icon bi bi-chevron-down ms-1'></i>
                    </span>
                  </button>
                  <label
                    className='form-check form-check-custom form-check-solid ms-2 d-flex flex-column'
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: 3 }}
                  >
                    <input
                      type='checkbox'
                      id={`select-all-${safeId}`}
                      checked={isGroupAllSelected(group.name)}
                      onChange={() => handleGroupSelectAll(group.name)}
                       className='form-check-input mb-1'
                    />All
                  </label>
                </div>
                <div
                  id={`accordion-body-${safeId}`}
                  className='accordion-collapse collapse'
                  aria-labelledby={`accordion-header-${safeId}`}
                  data-bs-parent='#permissionsAccordion'
                >
                  <div className='accordion-body px-4 py-3'>
                    <div className='d-flex flex-column gap-2'>
                      {group.actions.map((action) => (
                        <div key={action.id} className='form-check form-check-custom form-check-solid'>
                          <Form.Check
                            type='checkbox'
                            id={`${safeId}-${action.id}`}
                            checked={selectedPermissions[group.name]?.includes(action.id) || false}
                            onChange={() => handlePermissionChange(group.name, action.id)}
                            label={formatActionLabel(action.action)}
                            disabled={action.action === 'list' && isListDisabled(group.name)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

      {/* Render all threshold sections */}
      {renderThresholdSection('Masters', masterGroups, 'masters')}
      {renderThresholdSection('Reports', reportsGroups, 'reports')}
      {renderThresholdSection('Manage News', manageNewsGroups, 'manage-news')}
      {renderThresholdSection('Settings', settingsGroups, 'settings')}

      {formErrors?.permissions && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>{formErrors.permissions}</div>
        </div>
      )}
    </div>
  );
};

export default PermissionThreshold; 